import React from 'react';
import { IconButton } from '@mui/material';
import { Box, styled } from '@mui/system';

import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkOutlineIcon from '@mui/icons-material/BookmarkBorder';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutlined';
import HideIcon from '@mui/icons-material/VisibilityOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import * as ItemUtils from '../../common/ItemTools';
import CompletenessScore from '../common/CompletenessScore';
import { useCardShows } from '../common/card/useCardDensity';
import CardFavoriteAction from '../common/card/CardFavoriteAction';
import CardShareAction from '../common/card/CardShareAction';

const ACTION_ICON_SIZE = 18;

const ActionsRow = styled(Box)({
  display: 'flex',
  gap: 2,
  flexShrink: 0,
  alignItems: 'center',
});

const CompactActionButton = styled(IconButton)({
  padding: 4,
  borderRadius: 6,
  color: '#3c3c3c',
  '&:hover': { background: 'rgba(0,0,0,0.05)' },
  '& svg': {
    fontSize: ACTION_ICON_SIZE,
    width: ACTION_ICON_SIZE,
    height: ACTION_ICON_SIZE,
  },
});

// The footprint of a CompactActionButton, for a state the viewer can see but
// not change.
const StaticAction = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: 4,
  '& svg': {
    fontSize: ACTION_ICON_SIZE,
    width: ACTION_ICON_SIZE,
    height: ACTION_ICON_SIZE,
  },
});

const StyledDoneFilledIcon = styled(CheckCircle)({
  color: 'green',
});

const StyledDoneOutlineIcon = styled(CheckCircleOutline)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

function StyledDoneIcon({ read, ...props }) {
  return read
    ? <StyledDoneFilledIcon {...props} />
    : <StyledDoneOutlineIcon {...props} />;
}

const StyledHideIcon = styled(HideIcon, {
  shouldForwardProp: (prop) => prop !== 'hidden',
})(({ theme, hidden }) => ({
  ...(hidden && { color: theme.palette.error.main }),
}));

const StyledBookmarkFilledIcon = styled(BookmarkIcon)({
  color: '#1565c0',
});

const StyledBookmarkOutlineIcon = styled(BookmarkOutlineIcon)({});

function StyledBookmarkIcon({ checked, ...props }) {
  return checked
    ? <StyledBookmarkFilledIcon {...props} />
    : <StyledBookmarkOutlineIcon {...props} />;
}

/**
 * The footer's right half. Each action appears only when its callback was
 * given, which is how a namespace decides what the card can do.
 *
 * As the card runs out of room the actions leave for the ⋮ menu, which
 * `buildMenuOptions` fills with the same entries. State the viewer can see but
 * not change (hidden, favorite, completeness) has no menu to go to and drops a
 * step later. Zen mode takes the whole half.
 */
export default function CardActions({
  item, isPreview, selectionMode, isHidden, hasMenu, onMenuClick,
  onItemBookmark, onItemShare, onItemHide, onItemMarkReviewed, onItemFavorite,
}) {
  const completeness = ItemUtils.getItemCompleteness(item);
  const shows = useCardShows();

  const shareChanges = ItemUtils.shareChangesState(item.shareDiff);
  const shareTitle = ItemUtils.shareActionTitle(Boolean(item.hasPublicData), item.shareDiff);

  const showActions = shows('actions');
  const showMarkers = shows('markers');
  const showMenu = hasMenu && shows('menu');
  if (!showActions && !showMarkers && !showMenu) return null;

  // Published with nothing left to propose is a marker, not a button, so it
  // outlives the actions rather than following them into the menu.
  const canShare = onItemShare && item.hasPrivateData && !isHidden;
  const shareIsMarker = Boolean(item.hasPublicData) && shareChanges === 'none';

  return (
    <ActionsRow>
      {showActions && onItemBookmark && item.hasPublicData && (
        <CompactActionButton
          title={item.hasPrivateData ? 'Remove the private copy' : 'Save a private copy'}
          onClick={() => onItemBookmark(item)}
        >
          <StyledBookmarkIcon checked={Boolean(item.hasPrivateData)} />
        </CompactActionButton>
      )}
      {showMarkers && !isPreview && selectionMode && (
        <CompletenessScore score={completeness.score} missing={completeness.missing} />
      )}
      {canShare && (shareIsMarker ? showMarkers : showActions) && (
        <CardShareAction
          hasPublicData={Boolean(item.hasPublicData)}
          changes={shareChanges}
          title={shareTitle}
          onShare={() => onItemShare(item)}
        />
      )}
      {showActions && isHidden && onItemHide && (
        <CompactActionButton
          title="Hidden — click to unhide"
          onClick={() => onItemHide(item)}
        >
          <StyledHideIcon hidden />
        </CompactActionButton>
      )}
      {showMarkers && isHidden && !onItemHide && (
        <StaticAction title="Hidden">
          <StyledHideIcon hidden />
        </StaticAction>
      )}
      {showActions && onItemMarkReviewed && (
        <CompactActionButton onClick={() => onItemMarkReviewed(item)}>
          <StyledDoneIcon read={Boolean(item.timeReviewed)} />
        </CompactActionButton>
      )}
      {(onItemFavorite ? showActions : showMarkers) && (
        <CardFavoriteAction
          isFavorite={Boolean(item.isFavorite)}
          onToggle={onItemFavorite && (() => onItemFavorite(item))}
        />
      )}
      {showMenu && (
        <CompactActionButton onClick={onMenuClick}>
          <MoreVertIcon />
        </CompactActionButton>
      )}
    </ActionsRow>
  );
}
